<template>
  <div class="bot-bubble">
    <perfect-scrollbar class="border-solid bg-gray-bubble bubble-min-h text-white p-1 rounded-lg"
         :class="bubbleClasses"
         @click.self="$emit('click')">

      <div v-if="showTemplateIcon" class="relative">
        <blocks-icon :is-filled="true" class="absolute top-0 right-0 test-template-icon h-4 w-4 pt-px pr-px"/>
      </div>

      <div v-if="imageUrl" @click="$emit('select-image')">
        <img v-if="imageIsValid" :src="imageUrl" class="w-full"/>

        <div v-else class="p-2">
          <div v-if="imageIsPlaceholder">
            <span>🌟</span><span class="ml-3">Dynamic image (no preview available)</span>
          </div>

          <div v-else>
            <span>⚠️</span><span class="ml-3">Invalid image</span>
          </div>
        </div>
      </div>

      <div v-else
           class="w-full px-2 py-3 relative"
           :class="textClasses"
           @click="$emit('select-text')"
      >
        <a
          v-if="fileUrl"
          :href="fileUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="break-all underline cursor-pointer inline-block"
        >
          {{ fileUrl }}
        </a>
        <span v-else-if="renderHtml" class="rich-text break-words" v-html="text"/>
        <span v-else-if="originalContent" class="break-words" v-html="textWithBoldSubstitutions"/>
        <span v-else class="break-words" v-html="text"/>
        <slot name="checkbox"/>
      </div>

      <template v-if="buttons">
        <div v-for="(button, i) in buttons" :key="i"
             class="flex bg-white mt-2 text-gray-dark m-1 border-solid"
             :class="buttonClasses(i)"
             @click="$emit('select-button', i)">
          <span class="mx-auto my-2" v-html="renderSubstitutedText(button.title)"></span>
        </div>
      </template>
    </perfect-scrollbar>

    <div v-if="quickReplies && quickReplies.length > 0" class="col-span-4 col-start-2 flex flex-wrap justify-end mt-3">
      <div v-for="(qr, i) in quickReplies" :key="i"
           class="border border-solid rounded-full ml-1 mt-2 px-3 py-2"
           :class="quickReplyClasses(i)"
           @click="$emit('select-quick-reply', i)">
        <span v-html="renderSubstitutedText(qr.title)"></span>
      </div>
    </div>
  </div>
</template>

<script>
import contentUtils from '@/utils/content.utils'
import BlocksIcon from '@/assets/images/icons/BlocksIcon'
import variableRefsUtils from '@/utils/variable-refs.utils'
import conversationDetailModule from '@/store/modules/conversation-detail.module'
import { PerfectScrollbar } from 'vue2-perfect-scrollbar'

export default {
  components: { BlocksIcon, PerfectScrollbar },

  props: {
    bubbleClasses: { default: 'border-primary' },
    textClasses: { default: '' },
    buttonClasses: { type: Function, default: () => 'border-white' },
    quickReplyClasses: { type: Function, default: () => 'border-primary text-primary' },
    content: { type: Object, required: true },
    originalContent: { type: Object, default: null },
    showTemplateIcon: { type: Boolean, default: false },
    renderHtml: { type: Boolean, default: false }
  },

  computed: {
    text() {
      const textFromContent = contentUtils.getTextFromContent(this.content)
      const text = textFromContent ? this.substituteText(textFromContent) : ''
      return this.sanitizeRichText(text)
    },

    textWithBoldSubstitutions() {
      if (!this.originalContent) return this.text

      const originalText = contentUtils.getTextFromContent(this.originalContent)
      if (!originalText) return this.text

      const apiCallMessage = this.findApiCallMessage()
      const contextData = apiCallMessage && apiCallMessage.data.context ? apiCallMessage.data.context : null

      return contentUtils.markSubstitutedText(originalText, contextData)
    },

    buttons() {
      const buttons = contentUtils.getButtonsFromContent(this.content)
      if (!this.originalContent) return buttons

      const originalButtons = contentUtils.getButtonsFromContent(this.originalContent)
      if (!originalButtons) return buttons

      const apiCallMessage = this.findApiCallMessage()
      const contextData = apiCallMessage && apiCallMessage.data.context ? apiCallMessage.data.context : null

      return buttons.map((button, index) => {
        const originalButton = originalButtons[index]
        if (originalButton && originalButton.title) {
          return {
            ...button,
            title: contentUtils.markSubstitutedText(originalButton.title, contextData)
          }
        }
        return button
      })
    },

    quickReplies() {
      const quickReplies = contentUtils.getQuickRepliesFromContent(this.content)
      if (!this.originalContent) return quickReplies

      const originalQuickReplies = contentUtils.getQuickRepliesFromContent(this.originalContent)
      if (!originalQuickReplies) return quickReplies

      const apiCallMessage = this.findApiCallMessage()
      const contextData = apiCallMessage && apiCallMessage.data.context ? apiCallMessage.data.context : null

      return quickReplies.map((qr, index) => {
        const originalQr = originalQuickReplies[index]
        if (originalQr && originalQr.title) {
          return {
            ...qr,
            title: contentUtils.markSubstitutedText(originalQr.title, contextData)
          }
        }
        return qr
      })
    },

    imageUrl() {
      return contentUtils.getImageUrlFromContent(this.content)
    },

    fileUrl() {
      return contentUtils.getFileUrlFromContent(this.content)
    },

    imageIsValid() {
      if (this.imageIsPlaceholder) return false

      return this.imageUrl.toLowerCase().match(/jpg|jpeg|png|gif|webp/)
    },

    imageIsPlaceholder() {
      return /\${[^}]+}/.test(this.imageUrl)
    }
  },

  mounted() {
    this.setupContextTooltipListeners()
  },

  beforeDestroy() {
    this.removeContextTooltipListeners()
  },

  methods: {
    substituteText(text) {
      return variableRefsUtils.substituteVariableRefs(text)
    },

    renderSubstitutedText(text) {
      // `markSubstitutedText()` returns HTML spans with these exact markers.
      // We only skip the second variable-substitution pass for that generated markup
      if (
        typeof text === 'string' &&
        text.includes('class="context-substituted"') &&
        text.includes('data-context=')
      ) {
        return text
      }

      return this.substituteText(text)
    },

    findApiCallMessage() {
      const messages = conversationDetailModule.getMessages()
      return messages.find(msg => msg.type === 'remote_action' && msg.data.context)
    },

    setupContextTooltipListeners() {
      this.$nextTick(() => {
        const contextElements = this.$el.querySelectorAll('.context-substituted')
        contextElements.forEach(element => {
          element.addEventListener('mouseenter', this.handleContextMouseEnter)
          element.addEventListener('mouseleave', this.handleContextMouseLeave)
        })
      })
    },

    removeContextTooltipListeners() {
      const contextElements = this.$el.querySelectorAll('.context-substituted')
      contextElements.forEach(element => {
        element.removeEventListener('mouseenter', this.handleContextMouseEnter)
        element.removeEventListener('mouseleave', this.handleContextMouseLeave)
      })
    },

    handleContextMouseEnter(event) {
      const contextKey = event.target.getAttribute('data-context')
      if (contextKey) {
        this.$emit('context-tooltip-show', contextKey)
      }
    },

    handleContextMouseLeave(event) {
      this.$emit('context-tooltip-hide')
    }
  },

  updated() {
    this.removeContextTooltipListeners()
    this.setupContextTooltipListeners()
  }
}
</script>

<style lang="scss">
.bot-bubble {
  .bubble-shadow {
    // noinspection CssInvalidFunction - theme() belongs to TailWind
    box-shadow: 0 4px 11px 0 theme('colors.focus-a50');
  }

  .context-substituted {
    text-decoration: underline;
    text-decoration-style: dotted;
  }
}
</style>
